import type { ListRedirectsQuery, Redirect } from '@idpe/schema';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import { ConfirmDialog } from '~/components/ui/Modal.js';
import { DataTable, PaginationControls } from '~/components/ui/DataTable.js';
import { Field } from '~/components/ui/Field.js';
import { RedirectFormDialog } from '~/features/redirects/RedirectFormDialog.js';
import { useDeleteRedirect, useRedirectsList } from '~/features/redirects/hooks.js';
import { ApiError } from '~/lib/api.js';

const searchSchema = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1).optional(),
  perPage: z.coerce.number().int().min(1).max(200).default(50).optional(),
});

export const Route = createFileRoute('/_auth/redirects')({
  validateSearch: (search) => searchSchema.parse(search),
  component: RedirectsListPage,
});

function RedirectsListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Redirect | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Redirect | null>(null);
  const deleteMut = useDeleteRedirect();

  const query: Partial<ListRedirectsQuery> = {
    q: search.q,
    page: search.page ?? 1,
    perPage: search.perPage ?? 50,
  };

  const { data, isLoading, error, refetch } = useRedirectsList(query);

  function updateSearch(patch: Partial<typeof search>): void {
    navigate({
      to: '/redirects',
      search: { ...search, ...patch, page: patch.page ?? 1 },
    });
  }

  async function onConfirmDelete(): Promise<void> {
    if (!confirmDelete) return;
    try {
      await deleteMut.mutateAsync(confirmDelete.id);
      toast.success('Redirect removido.');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.displayMessage : 'Erro ao remover redirect.');
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Configurações</p>
        <h1 className="page-title">Links antigos</h1>
        <p className="page-sub">
          Quando um endereço do site muda, cadastre-o aqui apontando para o novo. Quem clicar no
          link antigo — num e-mail, num documento, num resultado de busca — chega ao lugar certo em
          vez de ver uma página de erro.
        </p>
      </header>

      <section>
        <div className="toolbar">
          <div className="toolbar__row">
            <div className="toolbar__filters">
              <Field
                label="Buscar"
                placeholder="origem, destino ou nota"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') updateSearch({ q: searchInput || undefined });
                }}
                onBlur={() => updateSearch({ q: searchInput || undefined })}
              />
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button variant="primary" onClick={() => setCreating(true)} fullWidth>
                  Novo redirect
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 'var(--s-5)' }}>
          <DataTable
            data={data?.data ?? []}
            loading={isLoading}
            error={
              error
                ? {
                    message: error.displayMessage,
                    retry: () => void refetch(),
                  }
                : null
            }
            keyExtractor={(r) => r.id}
            onRowClick={(r) => setEditing(r)}
            emptyMessage="Nenhum redirect cadastrado."
            emptyAction={
              <Button variant="secondary" onClick={() => setCreating(true)}>
                Criar primeiro redirect
              </Button>
            }
            columns={[
              {
                key: 'fromPath',
                header: 'De',
                mobileLine: 'primary',
                cell: (r) => <code style={{ fontSize: 'var(--fs-sm)' }}>{r.fromPath}</code>,
              },
              {
                key: 'toPath',
                header: 'Para',
                mobileLine: 'secondary',
                cell: (r) => (
                  <code style={{ fontSize: 'var(--fs-sm)', color: 'var(--fg-muted)' }}>
                    {r.toPath}
                  </code>
                ),
              },
              {
                key: 'statusCode',
                header: 'Status',
                mobileLine: 'meta',
                cell: (r) => (
                  <Badge
                    tone={r.statusCode === 301 || r.statusCode === 308 ? 'primary' : 'warning'}
                  >
                    {r.statusCode}
                  </Badge>
                ),
              },
              {
                key: 'nota',
                header: 'Nota',
                cell: (r) =>
                  r.nota ? (
                    <span style={{ color: 'var(--fg-muted)', fontSize: 'var(--fs-sm)' }}>
                      {r.nota}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--fg-muted)' }}>—</span>
                  ),
              },
              {
                key: 'actions',
                header: '',
                cell: (r) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDelete(r);
                    }}
                  >
                    Remover
                  </Button>
                ),
              },
            ]}
          />
        </div>

        {data && data.total > 0 && data.totalPages > 1 && (
          <PaginationControls
            page={data.page}
            perPage={data.perPage}
            total={data.total}
            totalPages={data.totalPages}
            onPageChange={(page) => updateSearch({ page })}
          />
        )}
      </section>

      <RedirectFormDialog open={creating} onClose={() => setCreating(false)} />
      <RedirectFormDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        redirect={editing}
      />
      <ConfirmDialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void onConfirmDelete()}
        title="Remover redirect?"
        description={`O redirect ${confirmDelete?.fromPath ?? ''} → ${confirmDelete?.toPath ?? ''} será apagado permanentemente.`}
        confirmLabel="Remover"
        variant="danger"
        loading={deleteMut.isPending}
      />
    </div>
  );
}
